/**
 * GameDrawWheelDatabase.js
 * 
 * This file provides database models and functions for managing the Game Draw Wheel
 * feature, including lottery tickets and game draws, using a hybrid approach that
 * keeps most data off-chain while using blockchain for fairness verification.
 */

// Mock database for development - in production this would connect to your actual database
const localStoragePrefix = 'palm_heist_';

/**
 * User Lottery Tickets Model
 * Stores a user's lottery tickets for each batch
 * 
 * Structure:
 * {
 *   userId: string,
 *   batchId: string,
 *   ticketCount: number,
 *   ticketDetails: [
 *     {
 *       id: string,
 *       cardId: string,
 *       timestamp: number,
 *       ticketValue: number (1-5)
 *     }
 *   ]
 * }
 */

/**
 * Batch Lottery Model
 * Stores the overall lottery information for a batch
 * 
 * Structure:
 * {
 *   batchId: string,
 *   totalTickets: number,
 *   participants: string[],
 *   isDrawComplete: boolean,
 *   drawTimestamp: number,
 *   drawResult: {
 *     winner: string,
 *     prizeAmount: number,
 *     randomSeed: string,
 *     isProcessed: boolean
 *   }
 * }
 */

/**
 * Gets a user's lottery tickets for a specific batch
 * @param {string} userId - The user ID
 * @param {string} batchId - The batch ID
 * @returns {Object} - The user's lottery tickets
 */
export const getUserLotteryTickets = (userId, batchId) => {
  const key = `${localStoragePrefix}user_lottery_tickets_${userId}_${batchId}`;
  const storedData = localStorage.getItem(key);
  
  if (!storedData) {
    // Initialize empty record if none exists
    const emptyRecord = {
      userId,
      batchId,
      ticketCount: 0,
      ticketDetails: []
    };
    localStorage.setItem(key, JSON.stringify(emptyRecord));
    return emptyRecord;
  }
  
  return JSON.parse(storedData);
};

/**
 * Gets the batch lottery information
 * @param {string} batchId - The batch ID
 * @returns {Object} - The batch lottery information
 */
export const getBatchLottery = (batchId) => {
  const key = `${localStoragePrefix}batch_lottery_${batchId}`;
  const storedData = localStorage.getItem(key);
  
  if (!storedData) {
    // Initialize empty record if none exists
    const emptyRecord = {
      batchId,
      totalTickets: 0,
      participants: [],
      isDrawComplete: false,
      drawTimestamp: null,
      drawResult: null
    };
    localStorage.setItem(key, JSON.stringify(emptyRecord));
    return emptyRecord;
  }
  
  return JSON.parse(storedData);
};

/**
 * Adds lottery tickets for a user
 * @param {string} userId - The user ID
 * @param {string} batchId - The batch ID
 * @param {string} cardId - The card ID
 * @param {number} ticketValue - The number of tickets to add (1-5)
 * @returns {Object} - The updated user lottery tickets
 */
export const addLotteryTickets = (userId, batchId, cardId, ticketValue) => {
  // Get current user tickets
  const userTickets = getUserLotteryTickets(userId, batchId);
  
  // Get current batch lottery
  const batchLottery = getBatchLottery(batchId);
  
  // Create new ticket entry
  const newTicket = {
    id: `ticket_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
    cardId,
    timestamp: Date.now(),
    ticketValue
  };
  
  // Update user tickets
  userTickets.ticketCount += ticketValue;
  userTickets.ticketDetails.push(newTicket);
  
  // Update batch lottery
  batchLottery.totalTickets += ticketValue;
  if (!batchLottery.participants.includes(userId)) {
    batchLottery.participants.push(userId);
  }
  
  // Save updates
  localStorage.setItem(`${localStoragePrefix}user_lottery_tickets_${userId}_${batchId}`, JSON.stringify(userTickets));
  localStorage.setItem(`${localStoragePrefix}batch_lottery_${batchId}`, JSON.stringify(batchLottery));
  
  return userTickets;
};

/**
 * Gets all participants' lottery tickets for a batch
 * @param {string} batchId - The batch ID
 * @returns {Array} - Array of user lottery tickets
 */
export const getAllParticipantTickets = (batchId) => {
  const batchLottery = getBatchLottery(batchId);
  const participants = batchLottery.participants;
  
  return participants.map(userId => {
    const userTickets = getUserLotteryTickets(userId, batchId);
    return {
      userId,
      ticketCount: userTickets.ticketCount,
      percentage: (userTickets.ticketCount / batchLottery.totalTickets) * 100
    };
  });
};

/**
 * Performs the game draw for a batch
 * @param {string} batchId - The batch ID
 * @param {string} randomSeed - The random seed from blockchain
 * @returns {Object} - The draw result
 */
export const performGameDraw = (batchId, randomSeed) => {
  const batchLottery = getBatchLottery(batchId);
  
  // Check if draw is already complete
  if (batchLottery.isDrawComplete) {
    return batchLottery.drawResult;
  }
  
  // Get all participants
  const participants = getAllParticipantTickets(batchId);
  
  // Create weighted selection array based on ticket counts
  const weightedParticipants = [];
  participants.forEach(participant => {
    for (let i = 0; i < participant.ticketCount; i++) {
      weightedParticipants.push(participant.userId);
    }
  });
  
  // Use the random seed to select a winner
  const seedValue = parseInt(randomSeed.substring(0, 8), 16);
  const winnerIndex = seedValue % weightedParticipants.length;
  const winnerId = weightedParticipants[winnerIndex];
  
  // Create draw result
  const drawResult = {
    winner: winnerId,
    prizeAmount: 1000, // Example prize amount
    randomSeed,
    isProcessed: false
  };
  
  // Update batch lottery
  batchLottery.isDrawComplete = true;
  batchLottery.drawTimestamp = Date.now();
  batchLottery.drawResult = drawResult;
  
  // Save updates
  localStorage.setItem(`${localStoragePrefix}batch_lottery_${batchId}`, JSON.stringify(batchLottery));
  
  return drawResult;
};

/**
 * Gets a user's win probability for a specific batch
 * @param {string} userId - The user ID
 * @param {string} batchId - The batch ID
 * @returns {number} - The win probability (0-100)
 */
export const getUserWinProbability = (userId, batchId) => {
  const userTickets = getUserLotteryTickets(userId, batchId);
  const batchLottery = getBatchLottery(batchId);
  
  if (batchLottery.totalTickets === 0) return 0;
  
  return (userTickets.ticketCount / batchLottery.totalTickets) * 100;
};

/**
 * Gets all batches where a user has lottery tickets
 * @param {string} userId - The user ID
 * @returns {Array} - Array of batch IDs
 */
export const getUserBatchesWithTickets = (userId) => {
  const result = [];
  
  // This is inefficient but works for local storage
  // In a real database, you'd use a query
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(`${localStoragePrefix}user_lottery_tickets_${userId}_`)) {
      const batchId = key.split('_').pop();
      const userTickets = JSON.parse(localStorage.getItem(key));
      
      if (userTickets.ticketCount > 0) {
        result.push(batchId);
      }
    }
  }
  
  return result;
};

/**
 * Gets the total number of lottery tickets a user has across all batches
 * @param {string} userId - The user ID
 * @returns {number} - The total number of tickets
 */
export const getUserTotalTickets = (userId) => {
  let totalTickets = 0;
  
  // This is inefficient but works for local storage
  // In a real database, you'd use a query
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(`${localStoragePrefix}user_lottery_tickets_${userId}_`)) {
      const userTickets = JSON.parse(localStorage.getItem(key));
      totalTickets += userTickets.ticketCount;
    }
  }
  
  return totalTickets;
};

/**
 * Marks a draw result as processed (prize claimed)
 * @param {string} batchId - The batch ID
 * @returns {boolean} - Whether the operation was successful
 */
export const markDrawResultProcessed = (batchId) => {
  const batchLottery = getBatchLottery(batchId);
  
  if (!batchLottery.isDrawComplete || !batchLottery.drawResult) {
    return false;
  }
  
  batchLottery.drawResult.isProcessed = true;
  localStorage.setItem(`${localStoragePrefix}batch_lottery_${batchId}`, JSON.stringify(batchLottery));
  
  return true;
};

/**
 * Gets all completed draws where the user is the winner
 * @param {string} userId - The user ID
 * @returns {Array} - Array of draw results
 */
export const getUserWinningDraws = (userId) => {
  const winningDraws = [];
  
  // This is inefficient but works for local storage
  // In a real database, you'd use a query
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(`${localStoragePrefix}batch_lottery_`)) {
      const batchLottery = JSON.parse(localStorage.getItem(key));
      
      if (batchLottery.isDrawComplete && 
          batchLottery.drawResult && 
          batchLottery.drawResult.winner === userId) {
        winningDraws.push({
          batchId: batchLottery.batchId,
          drawTimestamp: batchLottery.drawTimestamp,
          prizeAmount: batchLottery.drawResult.prizeAmount,
          isProcessed: batchLottery.drawResult.isProcessed
        });
      }
    }
  }
  
  return winningDraws;
};
